import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import CreateProjectModal from '../common/CreateProjectModal';

// Component imports
import EnquiriesHeader from './EnquiriesHeader';
import EnquiriesFilters from './EnquiriesFilters';
import EnquiriesSummaryCards from './EnquiriesSummaryCards';
import EnquiriesTable from './EnquiriesTable';
import EnquiriesEmptyState from './EnquiriesEmptyState';
import EnquiriesLoadingState from './EnquiriesLoadingState';
import EnquiriesErrorState from './EnquiriesErrorState';

import styles from './Enquiries.module.css';

const EnquiriesPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { jeweller } = useAuth();
  const navigate = useNavigate();

  const walkthrough = {
    title: 'How the enquiries page works',
    durationLabel: '4 min',
    embedUrl: 'https://player.vimeo.com/video/1170342206?badge=0&autopause=0&player_id=0&app_id=58479'
  };

  const fetchEnquiries = async () => {
    if (!jeweller?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const enquiriesQuery = query(
        collection(db, 'projects'),
        where('jewellerId', '==', jeweller.id)
      );
      
      const enquirySnapshot = await getDocs(enquiriesQuery);
      
      const enquiryData = enquirySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          customer: doc.data().customerInfo
        }))
        .filter(project => 
          project.status === 'new_enquiry' || 
          project.status === 'in_conversation' || 
          project.status === 'enquiry' ||
          project.status === 'archived'
        )
        .sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bDate - aDate;
        });
      
      setEnquiries(enquiryData);
      
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      setError('Failed to connect to database. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Filter enquiries based on active filter
  useEffect(() => {
    let filtered = [...enquiries];
    
    switch (activeFilter) {
      case 'new':
        filtered = enquiries.filter(e => e.status === 'new_enquiry');
        break;
      case 'conversation':
        filtered = enquiries.filter(e => e.status === 'in_conversation');
        break;
      case 'archived':
        filtered = enquiries.filter(e => e.status === 'archived');
        break;
      case 'all':
      default:
        // Show all except archived by default
        filtered = enquiries.filter(e => e.status !== 'archived');
        break;
    }
    
    setFilteredEnquiries(filtered);
  }, [enquiries, activeFilter]);

  useEffect(() => {
    fetchEnquiries();
  }, [jeweller]);

  const handleEnquiryCreated = () => {
    fetchEnquiries();
  };

  const handleRowClick = (enquiryId) => {
    navigate(`/projects/${enquiryId}`);
  };

  // Filter counts
  const filterCounts = {
    all: enquiries.filter(e => e.status !== 'archived').length,
    new: enquiries.filter(e => e.status === 'new_enquiry').length,
    conversation: enquiries.filter(e => e.status === 'in_conversation').length,
    archived: enquiries.filter(e => e.status === 'archived').length
  };

  // Summary calculations (excluding archived)
  const summaryData = {
    total: enquiries.filter(e => e.status !== 'archived').length,
    new: enquiries.filter(e => e.status === 'new_enquiry').length,
    inConversation: enquiries.filter(e => e.status === 'in_conversation').length
  };

  if (loading) {
    return <EnquiriesLoadingState />;
  }

  if (error) {
    return <EnquiriesErrorState error={error} />;
  }

  return (
    <div className={styles.container}>
      <EnquiriesHeader 
        onNewEnquiry={() => setShowCreateModal(true)}
        walkthrough={walkthrough}
      />

      <EnquiriesSummaryCards summaryData={summaryData} />

      <EnquiriesFilters 
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        filterCounts={filterCounts}
      />

      {filteredEnquiries.length === 0 ? (
        <EnquiriesEmptyState 
          onCreateEnquiry={() => setShowCreateModal(true)}
          activeFilter={activeFilter}
        />
      ) : (
        <EnquiriesTable 
          enquiries={filteredEnquiries}
          onRowClick={handleRowClick}
        />
      )}

      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProjectCreated={handleEnquiryCreated}
        title="Create New Enquiry"
        description="Create a new customer enquiry to track potential projects"
      />
    </div>
  );
};

export default EnquiriesPage;